import { distributeBuildLaborHours, distributeLinearLaborHours } from '@/components/composables/laborDistribution.ts'
import type { FieldType, ICollection, IForecastIndex, IForecastStatus, IProjectLaborDistribution } from '@/interfaces'
import { Collection, ForecastStatus } from '@/models'

import { addToJSON } from './helper'

// export interface IForecastIndex extends IForecastBase {
// 	subjobId: number
// 	subjobNumber: string
// 	subjobName: string
// 	forecastId: string
// 	buildLocation: string
// 	salesLocation: string
// 	clientId: string
// 	clientName: string
// 	showCode: string
// 	showPrimaryLocation: string
// 	status: string
// 	dueDate: Date | string
// 	shipDate: Date | string
// 	committed: Date | string
// 	productionKickoffDate: Date | string
// 	designDate: Date | string
// 	estimatingKickoffDate: Date | string
// 	detailingDate: Date | string
// 	signedQuoteWODate: Date | string
// 	graphicsDate: Date | string
// 	postShowReviewDate: Date | string
// 	probabilityOfSales: string
// 	timeline: string
// 	estimatedContract: number
// 	hours: number
// 	hoursProbability: number
// 	forecastType: string
// 	notes: string
// 	isForecast: boolean
// 	statusProbOfSale: string
// 	statusEstContract: string
// 	statusShipDate: string
// 	statusProductionKickoffDate: string
// 	forecastStatus: ICollection<IForecastStatus>
// }

export class ForecastIndex implements IForecastIndex {
	[key: string]: FieldType
	id: number
	subjobId: number
	subjobNumber: string
	subjobName: string
	forecastId: number
	status: string
	subjobType: string
	subjobContractingState: string
	subjobContractingStateAlias: string
	subjobProductionState: string
	subjobProductionStateAlias: string
	subjobStatus: string
	jobStatus: string
	buildLocation: string
	salesLocation: string
	clientId: string
	clientName: string
	showCode: string
	showPrimaryLocation: string
	showName: string
	salesTeam: number
	salesTeamName: string
	dueDate: Date | string
	shipDate: Date | string
	committed: Date | string
	productionKickoffDate: Date | string
	designDate: Date | string
	estimatingKickoffDate: Date | string
	detailingDate: Date | string
	signedQuoteWODate: Date | string
	graphicsDate: Date | string
	postShowReviewDate: Date | string
	actualSignDate: Date | string
	workOrderDate: Date | string
	probabilityOfSales: string
	timeline: string
	estimatedContract: number
	estimatedContractSource: string
	hours: number
	hoursProbability: number
	forecastType: string
	notes: string
	isForecast: boolean
	statusProbOfSale: string
	statusEstContract: string
	statusShipDate: string
	statusProductionKickoffDate: string
	weightedHoursOverride: boolean
	forecastStatus: ICollection<IForecastStatus>
	laborCalc: string | null
	rfp: boolean

	constructor({
		id,
		subjobId,
		subjobNumber,
		subjobName,
		forecastId,
		status,
		subjobType,
		subjobContractingState,
		subjobContractingStateAlias,
		subjobProductionState,
		subjobProductionStateAlias,
		subjobStatus,
		jobStatus,
		buildLocation,
		salesLocation,
		clientId,
		clientName,
		showCode,
		showPrimaryLocation,
		showName,
		salesTeam,
		salesTeamName,
		dueDate = new Date(),
		shipDate = new Date(),
		committed,
		productionKickoffDate = new Date(),
		designDate = new Date(),
		estimatingKickoffDate = new Date(),
		detailingDate = new Date(),
		signedQuoteWODate = new Date(),
		graphicsDate = new Date(),
		postShowReviewDate = new Date(),
		actualSignDate = new Date(),
		workOrderDate = new Date(),
		probabilityOfSales,
		timeline,
		estimatedContract,
		estimatedContractSource,
		hours,
		hoursProbability,
		forecastType,
		notes,
		isForecast,
		statusProbOfSale,
		statusEstContract,
		statusShipDate,
		statusProductionKickoffDate,
		weightedHoursOverride,
		forecastStatus,
		laborCalc,
		rfp
	}: Partial<IForecastIndex> = {}) {
		this.id = id || 0
		this.subjobId = subjobId || 0
		this.subjobNumber = subjobNumber || ''
		this.subjobName = subjobName || ''
		this.forecastId = forecastId || 0
		this.status = status || ''
		this.subjobType = subjobType || ''
		this.subjobContractingState = subjobContractingState || ''
		this.subjobContractingStateAlias = subjobContractingStateAlias || ''
		this.subjobProductionState = subjobProductionState || ''
		this.subjobProductionStateAlias = subjobProductionStateAlias || ''
		this.subjobStatus = subjobStatus || ''
		this.jobStatus = jobStatus || ''
		this.buildLocation = buildLocation || ''
		this.salesLocation = salesLocation || ''
		this.clientId = clientId || ''
		this.clientName = clientName || ''
		this.showCode = showCode || ''
		this.showPrimaryLocation = showPrimaryLocation || ''
		this.showName = showName || ''
		this.salesTeam = salesTeam || 0
		this.salesTeamName = salesTeamName || ''
		this.dueDate = new Date(dueDate)
		this.shipDate = new Date(shipDate)
		this.committed = committed || ''
		this.productionKickoffDate = new Date(productionKickoffDate)
		this.designDate = new Date(designDate)
		this.estimatingKickoffDate = new Date(estimatingKickoffDate)
		this.detailingDate = new Date(detailingDate)
		this.signedQuoteWODate = new Date(signedQuoteWODate)
		this.graphicsDate = new Date(graphicsDate)
		this.postShowReviewDate = new Date(postShowReviewDate)
		this.actualSignDate = new Date(actualSignDate)
		this.workOrderDate = new Date(workOrderDate)
		this.probabilityOfSales = probabilityOfSales || ''
		this.timeline = timeline || ''
		this.estimatedContract = estimatedContract || 0
		this.estimatedContractSource = estimatedContractSource || ''
		this.hours = hours || 0
		this.hoursProbability = hoursProbability || 0
		this.forecastType = forecastType || ''
		this.notes = notes || ''
		this.isForecast = isForecast || false
		this.statusProbOfSale = statusProbOfSale || ''
		this.statusEstContract = statusEstContract || ''
		this.statusShipDate = statusShipDate || ''
		this.statusProductionKickoffDate = statusProductionKickoffDate || ''
		this.weightedHoursOverride = weightedHoursOverride || false
		this.forecastStatus = new Collection(ForecastStatus, forecastStatus || [])
		this.laborCalc = laborCalc ?? null
		this.rfp = rfp || false
		addToJSON(ForecastIndex)
	}

	get showNameFull(): string {
		return `${this.showName}\n${this.showPrimaryLocation}`
	}

	get weightedHours(): number {
		if (parseFloat(this.probabilityOfSales) === 0.8 || this.weightedHoursOverride === true) return this.hours
		return Math.round((this.hours * parseFloat(this.probabilityOfSales) + Number.EPSILON) * 100) / 100
	}

	get laborCalcDisplay(): string {
		if (this.laborCalc === 'linear') {
			return 'Linear'
		}
		return 'Historical'
	}

	get rfpDisplay(): string {
		return this.rfp ? 'Competitive' : 'Noncompetitive'
	}

	get laborEntries(): IProjectLaborDistribution {
		// Check laborCalc field to determine distribution method
		if (this.laborCalc === 'linear') {
			// Use linear (even) distribution
			return distributeLinearLaborHours(
				this.id,
				this.buildLocation,
				this.weightedHours,
				new Date(this.productionKickoffDate),
				new Date(this.shipDate)
			)
		} else {
			// Default to historical curve-based distribution
			// (when laborCalc is 'historical', null, or any other value)
			return distributeBuildLaborHours(
				this.id,
				this.buildLocation,
				this.weightedHours,
				new Date(this.productionKickoffDate),
				new Date(this.shipDate)
			)
		}
	}
	// function calculateHours(number) {
	// 	return
	// }
}
